"use client";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { getSearchHistory, type SearchHistoryItem } from "@/lib/search-history-service";
import { Clock, Search, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";

interface RecipeSearchBarProps {
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly placeholder?: string;
  readonly onClear?: () => void;
  readonly className?: string;
  readonly showHistory?: boolean;
  readonly onFocus?: () => void;
}

export function RecipeSearchBar({
  value,
  onChange,
  placeholder = "Search recipes...",
  onClear,
  className = "",
  showHistory = false,
  onFocus: onFocusProp,
}: RecipeSearchBarProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [searchHistory, setSearchHistory] = useState<SearchHistoryItem[]>([]);
  const [showHistoryDropdown, setShowHistoryDropdown] = useState(false);

  useEffect(() => {
    if (showHistory) {
       
      setSearchHistory(getSearchHistory());
    }
  }, [showHistory]);

  const handleClear = () => {
    onChange("");
    onClear?.();
    inputRef.current?.focus();
  };

  const handleHistorySelect = (item: SearchHistoryItem) => {
    onChange(item.query);
    setShowHistoryDropdown(false);
    inputRef.current?.focus();
  };

  const handleFocus = () => {
    if (showHistory && searchHistory.length > 0) {
      setShowHistoryDropdown(true);
    }
    onFocusProp?.();
  };

  const handleBlur = () => {
    // Delay to allow clicking on history items
    setTimeout(() => setShowHistoryDropdown(false), 200);
  };

  return (
    <div className={`relative ${className}`}>
      <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
      <Input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder={placeholder}
        className="pl-9 pr-9"
      />
      {value && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleClear}
          className="absolute right-1 top-1/2 h-7 w-7 -translate-y-1/2 p-0"
          aria-label="Clear search"
        >
          <X className="h-4 w-4" />
        </Button>
      )}
      {showHistory && showHistoryDropdown && searchHistory.length > 0 && (
        <div className="absolute top-full z-50 mt-1 w-full rounded-md border bg-popover shadow-md">
          <div className="p-2">
            <div className="mb-2 px-2 text-xs font-semibold text-muted-foreground">Recent Searches</div>
            {searchHistory.slice(0, 5).map((item) => (
              <button
                key={item.timestamp}
                type="button"
                onClick={() => handleHistorySelect(item)}
                className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent"
              >
                <Clock className="h-3 w-3 text-muted-foreground" />
                <span className="flex-1 text-left">{item.query}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

